import { z } from "zod";

import { PERMISSION_DOMAINS } from "../permissions";

/**
 * BİLDİRİM TESLİM VE TERCİH PARAMETRELERİ (CLOS-187 `[169]`).
 *
 * Kartın açık karar kuralı: *"Parametreler hard-code edilmez."* Poll aralığı, e-posta
 * tekrar politikası ve kapatılamaz bildirim kümesi burada, sürümlü konfigde yaşıyor.
 */

/**
 * ⚠ KAPATILAMAZ OLAYLAR NEDEN KONFİGDE — ve neden matriste DEĞİL.
 *
 * Kart *"zorunlu güvenlik bildirimleri kullanıcı tercihiyle kapatılamaz"* diyor ve bu
 * sınıfın "kanonik olay sınıflandırmasından" geleceğini varsayıyor. **Ölçüldü: böyle bir
 * sınıflandırma yok.**
 *
 * `EVENT_ROUTING`'de `criticality: "normal" | "high"` var ama `high` olan üç olay
 * (`organization.closed`, `file.scan_infected`, `contract.signature_request_settled`)
 * güvenlik sınıfı değil — öncelik ile zorunluluk farklı kavramlar. `EVENT_CATALOG`'daki
 * `sensitivity` (operational/personal/confidential) ise veri hassasiyeti; en güvenlik
 * olan olay `file.scan_infected` orada `operational` görünüyor.
 *
 * İki seçenek vardı: (a) `criticality: "high"`i "kapatılamaz" diye yeniden yorumlamak,
 * (b) kümeyi konfige almak. (a) reddedildi — iki ayrı kavramı tek alana bindirmek, yarın
 * "yüksek öncelikli ama isteğe bağlı" bir bildirim gerektiğinde çözümsüz kalırdı ve
 * CLOS-280 sözleşmesinin anlamını sessizce değiştirirdi.
 *
 * ⚠ AŞAĞIDAKİ BAŞLANGIÇ KÜMESİ ÜRÜN ONAYI BEKLİYOR (CLOS-856'ya soru olarak yazıldı).
 * Ölçüt olarak "kullanıcının kontrolü dışında gerçekleşen ve onun güvenlik durumunu
 * doğrudan değiştiren olay" alındı; kapsam genişletme/daraltma ürün kararıdır.
 */
export const notificationConfigSchema = z.object({
  version: z.literal(1),

  /**
   * Kontrollü polling aralığı (ADR-0037).
   *
   * ⚠ Bilinen sınır: bir bildirimin görünme gecikmesi EN FAZLA bu kadar. "Yakın gerçek
   * zamanlı" ifadesi bu ürün için "aralık kadar" demek; alt sınır, boşta bekleyen
   * sekmelerin sunucuya yük bindirmesini engelliyor.
   */
  pollIntervalMs: z.number().int().min(5_000).max(300_000),

  /**
   * Kullanıcı tercihiyle kapatılamayan olaylar.
   *
   * ⚠ Boş olamaz: boş bırakılırsa "zorunlu güvenlik bildirimi kapatılamaz" kriteri
   * sessizce hiçbir olayı korumaz hâle gelirdi — ölçmeyen bir kural, olmayan kuraldır.
   */
  mandatoryEvents: z.array(z.string().min(1)).min(1),

  email: z.object({
    /**
     * Aynı olay için e-posta tekrarının engelleneceği pencere.
     *
     * ⚠ Uygulama içi kayıt bundan ETKİLENMEZ. Kartın kriteri açık: *"e-posta teslim
     * hataları uygulama içi kaydı silmez."* Tekrar politikası yalnız kanalı susturur,
     * bildirimin kendisini değil.
     */
    dedupeWindowMinutes: z.number().int().min(1).max(1440),
    /** Teslim başarısız olduğunda kaç kez yeniden denenecek. */
    maxRetries: z.number().int().min(0).max(10),
  }),

  /**
   * Kategori bazlı VARSAYILAN tercihler.
   *
   * ⚠ Kullanıcı kaydı olmayan bir kategori için karar buradan okunuyor; yeni bir domain
   * eklendiğinde kullanıcıya sormadan makul bir varsayılan uygulanabilsin diye. Kullanıcı
   * bir kategoriyi kapattığında kararı onun kaydı veriyor — zorunlu olaylar hariç.
   */
  defaults: z.record(
    z.enum(PERMISSION_DOMAINS),
    z.object({ inApp: z.boolean(), email: z.boolean() }),
  ),
});

export type NotificationConfig = z.infer<typeof notificationConfigSchema>;

/** Tercihin uygulandığı kanallar. */
export const NOTIFICATION_CHANNELS = ["inApp", "email"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_CHANNEL_NAMES: Record<NotificationChannel, string> = {
  inApp: "Uygulama içi",
  email: "E-posta",
};

/**
 * TERCİH GÜNCELLEME GÖVDESİ (CLOS-187 `[169]`).
 *
 * ⚠ Kategori ve kanal AYRI alanlar, tek bir "ayarlar nesnesi" değil. Bütün tercih
 * kümesini tek istekte gönderseydik, iki sekmeden yapılan iki değişiklikten ikincisi
 * birincisini sessizce ezerdi — kullanıcı kapattığı bir kategoriyi geri açılmış bulurdu.
 * Tek kategori güncelleyen istek, o çakışmayı yapısal olarak imkânsız kılıyor.
 */
export const notificationPreferenceUpdateSchema = z.object({
  category: z.enum(PERMISSION_DOMAINS, { message: "Geçerli bir kategori seçin" }),
  inApp: z.boolean({ message: "Uygulama içi tercihi zorunludur" }),
  email: z.boolean({ message: "E-posta tercihi zorunludur" }),
});
export type NotificationPreferenceUpdate = z.infer<typeof notificationPreferenceUpdateSchema>;

/** Kullanıcıya dönen tercih satırı — kaydı olmayan kategori varsayılanıyla geliyor. */
export const notificationPreferenceSchema = z.object({
  category: z.enum(PERMISSION_DOMAINS),
  inApp: z.boolean(),
  email: z.boolean(),
  /**
   * ⚠ Bu kategoride KAPATILAMAZ olay var mı.
   *
   * Arayüz bunu bilmeden doğru şeyi gösteremez: kullanıcı e-postayı kapattığında yine de
   * güvenlik bildirimi alacaksa, bunu önceden söylemek gerekir. Sonradan "neden hâlâ
   * geliyor?" sorusu, kapatma sözünün tutulmadığı izlenimi bırakırdı.
   */
  hasMandatory: z.boolean(),
});
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;
